import { useState, useEffect, useCallback } from "react";
import { Modal, Form, Typography, Button } from "antd";

import ResourceField from "./ResourceField";

import { getAttachableObjects, attachObjects } from "@/api/task-templates";
import { getDefinitionOptions } from "@/api/definitions";

const { Text } = Typography;
const RESOURCE_DEFINITION_TYPES = ["repository", "object"];

// Окно для прикрепления ресурсов к шаблону задачи
const AttachingObjectsToTemplateModal = ({
  unit_uuid,
  template_uuid = null,
  visible,
  onSave,
  onCancel,
  width = 600,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [objectsOptionsMap, setObjectsOptionsMap] = useState({});
  const [definitionsOptions, setDefinitionsOptions] = useState([]);

  const fetchDefinitionsOptions = useCallback(async () => {
    try {
      const definitions = await getDefinitionOptions();
      setDefinitionsOptions(
        definitions.filter(({ type }) =>
          RESOURCE_DEFINITION_TYPES.includes(type),
        ),
      );
    } catch (error) {
      setError(error.message);
    }
  }, []);

  const fetchObjectsOptionsForField = useCallback(
    async (definition_uuid, fieldName) => {
      if (!definition_uuid) return;
      try {
        const objects = await getAttachableObjects({
          template_uuid,
          definition_uuid,
        });
        setObjectsOptionsMap((prev) => ({ ...prev, [fieldName]: objects }));
      } catch (error) {
        setError(error.message);
      }
    },
    [template_uuid],
  );

  useEffect(() => {
    fetchDefinitionsOptions();
  }, [unit_uuid, fetchDefinitionsOptions]);

  const handleCancel = () => {
    form.resetFields();
    setObjectsOptionsMap({});
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    setError(null);
    setIsLoading(true);

    try {
      await attachObjects({
        template_uuid,
        resources: values.resources.reduce((acc, curr) => {
          if (curr.objects?.length > 0) {
            acc.push(
              ...curr.objects.map((object) => ({
                object_uuid: object,
                definition_uuid: curr.definition,
              })),
            );
          }
          return acc;
        }, []),
      });

      onSave();
      form.resetFields();
      setObjectsOptionsMap({});
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () =>
    form
      .validateFields()
      .then((values) => {
        handleSave(values);
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });

  return (
    <Modal
      maskClosable={false}
      title="Добавить ресурсы в шаблон задачи"
      open={visible}
      width={width}
      onOk={onOk}
      onCancel={handleCancel}
      okText="Добавить"
      cancelText="Отмена"
      okButtonProps={{ loading: isLoading }}
    >
      <Form form={form} layout="vertical" initialValues={{ resources: [{}] }}>
        <Form.List name="resources">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <ResourceField
                  key={field.key}
                  form={form}
                  field={field}
                  fieldsCount={fields.length}
                  definitionsOptions={definitionsOptions}
                  objectsOptionsMap={objectsOptionsMap}
                  fetchObjectsOptionsForField={fetchObjectsOptionsForField}
                  setObjectsOptionsMap={setObjectsOptionsMap}
                  remove={remove}
                />
              ))}
              <Form.Item>
                <Button color="default" variant="filled" onClick={() => add()}>
                  Добавить тип
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        {error && <Text type="danger">{error}</Text>}
      </Form>
    </Modal>
  );
};

export default AttachingObjectsToTemplateModal;
